import {computed, effect, inject, Injectable, OnDestroy, signal} from '@angular/core';
import {Router} from '@angular/router';
import {BehaviorSubject, Subscription} from 'rxjs';
import {MatSnackBar} from '@angular/material/snack-bar';
import {
  CreatedPatient,
  Events,
  LocalStorageService,
  Patient,
  Practice,
  QueryPatient,
  sanitizeUrlName,
  SessionStorageService,
  Tab as TabInterface,
  UserTabsRepositoryService,
  WebSocketService,
} from '@xmedical/core';
import {CoreService} from '../services/core.service';
import {CoreEventsService} from '../services/core-events.service';
import {AuthenticationService} from '../auth/authentication.service';
import {PracticeStore} from './practice.store';
import {UserStore} from './user.store';

/**
 * TabsStore
 *
 * Manages the patient tab bar state using signals.
 *
 * Active-tab semantics:
 * - All five `openTabWith*` methods activate the new/existing tab (user-initiated).
 * - `closeTab` picks a neighbor (right-when-leftmost-else-left).
 * - `activateTabByProfileId` activates explicitly.
 * - `ensureTabExists` activates the matching tab — it's only called from the
 *   patient route's deep-link / cold-refresh path, where the URL is the
 *   source of truth for which patient is on screen.
 * - Pusher-driven `loadTabs` NEVER changes activeTabIndex unless the active
 *   tab disappeared from the list.
 *
 * Practice switch:
 * - Old Pusher channel is unsubscribed.
 * - In-memory tabs are cleared and activeTabIndex set to -1.
 * - Old practice's session-storage cache (`user-tabs-{oldId}`) is preserved
 *   for fast restore on switch-back.
 * - Router navigates to /dashboard (the previous URL referenced a patient
 *   from the old practice and is invalid in the new context).
 * - New practice is hydrated from session storage, then loaded from API.
 *
 * Eviction (when over `maxTabs`):
 * - Walks tabs from index 0 forward and evicts the first non-active tab.
 * - Active tab is never evicted.
 */
@Injectable({providedIn: 'root'})
export class TabsStore implements OnDestroy {
  private router = inject(Router);
  private coreService = inject(CoreService);
  private coreEventService = inject(CoreEventsService);
  private localStorage = inject(LocalStorageService);
  private sessionStorage = inject(SessionStorageService);
  private userTabsRepository = inject(UserTabsRepositoryService);
  private authService = inject(AuthenticationService);
  private pushService = inject(WebSocketService);
  private practiceStore = inject(PracticeStore);
  private userStore = inject(UserStore);
  private snackBar = inject(MatSnackBar);

  // State signals
  readonly tabs = signal<Tab[]>([]);
  readonly activeTabIndex = signal<number>(-1);

  // Computed
  readonly hasTabs = computed(() => this.tabs().length > 0);
  readonly hasActiveTab = computed(() => this.activeTabIndex() > -1);
  readonly activeTab = computed(() => this.tabs()[this.activeTabIndex()] ?? null);

  // Backwards-compat alias for existing consumers (calendar token, layouts)
  readonly hasTabsSignal = this.hasTabs;

  // Event stream — preserved for external consumers; tab-bar now uses signals
  private tabBehaviourSubject = new BehaviorSubject<TabEvent>(new TabEvent('tab_service_initialized'));
  readonly events$ = this.tabBehaviourSubject.asObservable();

  private readonly defaultRoute = '/dashboard';
  private currentPractice: Practice | null = null;
  private channelName = '';
  private maxTabs = -1;
  private defaultUserPath = '';
  private practiceLoadToken = 0;

  private readonly authSubscription: Subscription;
  private readonly coreSubscription: Subscription;

  private readonly storesReady = computed(() =>
    this.practiceStore.loaded() && this.userStore.loaded()
  );

  constructor() {
    this.initializeTabs();

    this.authSubscription = this.authService.events$.subscribe(event => {
      if (event.type === 'login') {
        this.initializeTabs();
      }
    });

    this.coreSubscription = this.coreEventService.events$.subscribe(event => {
      if (event.type === 'user_settings_loaded') {
        this.setupMaxTabs();
      }
    });

    this.setupMaxTabs();

    // Cold-init: stores become ready and we don't yet have a practice bound.
    effect(() => {
      if (this.storesReady() && !this.currentPractice) {
        this.initializeTabs();
      }
    });

    // Practice switch: react when the selected practice id changes after init.
    effect(() => {
      const selected = this.practiceStore.selectedPractice();
      if (!selected) return;
      if (!this.currentPractice) return; // cold-init path handles this
      if (selected.id === this.currentPractice.id) return;
      this.handlePracticeChange(selected);
    });
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
    this.coreSubscription?.unsubscribe();
    if (this.channelName) {
      this.pushService.unsubscribe(this.channelName, Events.UserTabsUpdated);
    }
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  openTabWithNewPatient(patient: CreatedPatient): void {
    let route = `/patients/switch/${patient.profileId}/${patient.name.sanitizeUrl()}`;
    if (this.defaultUserPath.length > 0) {
      route += '/' + this.defaultUserPath;
    }
    this.openTab(patient.name, patient.id, patient.profileId, route);
  }

  openTabWithPatientInEditMode(patient: QueryPatient): void {
    if (!patient?.profileId) {
      this.snackBar.open('Unable to open patient in new tab.', 'Close', {duration: 5000});
      return;
    }
    const route = `/patients/switch/${patient.profileId}/${patient.name.sanitizeUrl()}/edit`;
    this.openTab(patient.name, patient.id, patient.profileId, route);
  }

  openTabWithSearchPatient(patient: QueryPatient): void {
    if (!patient?.profileId) {
      this.snackBar.open('Unable to open patient in new tab.', 'Close', {duration: 5000});
      return;
    }
    let route = `/patients/switch/${patient.profileId}/${sanitizeUrlName(patient.name)}`;
    if (this.defaultUserPath.length > 0) {
      route += '/' + this.defaultUserPath;
    }
    this.openTab(patient.name, patient.id, patient.profileId, route);
  }

  openTabWithPatientAtClinical(patient: QueryPatient): void {
    if (!patient?.profileId) {
      this.snackBar.open('Unable to open patient in new tab.', 'Close', {duration: 5000});
      return;
    }
    const route = `/patients/switch/${patient.profileId}/${sanitizeUrlName(patient.name)}/clinical`;
    this.openTab(patient.name, patient.id, patient.profileId, route);
  }

  openTabWithPatient(patient: Patient): void {
    if (!patient?.profileId) {
      this.snackBar.open('Unable to open patient in new tab.', 'Close', {duration: 5000});
      return;
    }
    let route = `/patients/switch/${patient.profileId}/${sanitizeUrlName(patient.name)}`;
    if (this.defaultUserPath.length > 0) {
      route += '/' + this.defaultUserPath;
    }
    this.openTab(patient.name, patient.id, patient.profileId, route);
  }

  /**
   * Idempotent registration for the deep-link / cold-refresh path.
   * The router has already committed to this patient, so we always
   * activate the matching tab — it IS the patient on screen.
   */
  ensureTabExists(title: string, patientId: number, profileId: number): void {
    this.ensureInitialized();
    const route = `/patients/switch/${profileId}/${sanitizeUrlName(title)}`;
    const tab = new Tab(title, patientId, profileId, route);

    const tabs = [...this.tabs()];
    const existingIndex = tabs.findIndex(t => TabsStore.areTabsEqual(t, tab));
    let arrayChanged = false;
    let newActiveIndex = this.activeTabIndex();

    if (existingIndex === -1) {
      tabs.push(tab);
      arrayChanged = true;
      newActiveIndex = tabs.length - 1;
    } else {
      newActiveIndex = existingIndex;
    }

    const indexChanged = newActiveIndex !== this.activeTabIndex();

    if (arrayChanged) this.tabs.set(tabs);
    if (indexChanged) this.activeTabIndex.set(newActiveIndex);

    if (arrayChanged || indexChanged) {
      this.tabBehaviourSubject.next(new TabEvent('tab_activated'));
    }

    if (arrayChanged || indexChanged) {
      this.saveTabs();
    }

    this.enforceMaxTabs();
  }

  updateTabRoute(profileId: number, name: string, submodules: string[]): void {
    if (!this.hasTabs() || !this.hasActiveTab()) {
      return;
    }
    setTimeout(() => {
      const tabs = [...this.tabs()];
      const index = tabs.findIndex(t => t.profileId === profileId);
      if (index > -1) {
        let route = `/patients/switch/${profileId}/${sanitizeUrlName(name)}`;
        submodules.forEach(module => {
          route += `/${module}`;
        });
        tabs[index] = new Tab(tabs[index].title, tabs[index].patientId, tabs[index].profileId, route);
        this.tabs.set(tabs);
      }
    });
  }

  closeTab(index: number): void {
    this.ensureInitialized();
    const tabs = [...this.tabs()];
    if (!tabs[index]) return;

    const removedTab = tabs[index];
    const isCurrentTabActive = this.activeTabIndex() === index;
    const previousIndex = index === 0 ? index + 1 : index - 1;
    const isLastTabClosing = tabs.length === 1;

    tabs.splice(index, 1);
    this.tabs.set(tabs);

    // Adjust activeTabIndex to track its tab, or reset on close-of-active.
    let nextActive = this.activeTabIndex();
    if (isCurrentTabActive) {
      nextActive = -1; // will be set by activateTab below if applicable
    } else if (index < this.activeTabIndex()) {
      nextActive = this.activeTabIndex() - 1;
    }
    this.activeTabIndex.set(nextActive);

    this.saveTabs();
    this.removePatientFromStorage(removedTab.profileId);

    if (isCurrentTabActive && !isLastTabClosing) {
      // previousIndex was computed against the pre-splice array; clamp to the new array.
      const newIndex = Math.min(previousIndex, tabs.length - 1);
      this.activateTab(newIndex);
    }

    if (tabs.length === 0) {
      this.router.navigateByUrl(this.defaultRoute);
    }

    this.tabBehaviourSubject.next(new TabEvent('tab_closed'));
  }

  activateTabByProfileId(id: number): void {
    const idx = this.tabs().findIndex(t => t.profileId === id);
    this.activeTabIndex.set(idx);
  }

  getTabs(): Tab[] {
    return this.tabs();
  }

  getActiveTab(): number {
    return this.activeTabIndex();
  }

  // ---------------------------------------------------------------------------
  // Internal
  // ---------------------------------------------------------------------------

  private openTab(title: string, patientId: number, profileId: number, route: string): void {
    this.ensureInitialized();
    const tab = new Tab(title, patientId, profileId, route);
    const tabs = [...this.tabs()];
    let existingIndex = tabs.findIndex(t => TabsStore.areTabsEqual(t, tab));

    if (existingIndex === -1) {
      tabs.push(tab);
      this.tabs.set(tabs);
      existingIndex = tabs.length - 1;
    }

    this.saveTabs();
    this.activateTab(existingIndex);
    this.enforceMaxTabs();
  }

  private activateTab(index: number): void {
    const tabs = this.tabs();
    if (!tabs[index]) return;
    this.activeTabIndex.set(index);
    this.tabBehaviourSubject.next(new TabEvent('tab_activated'));
    this.router.navigateByUrl(tabs[index].route);
  }

  private initializeTabs(): void {
    if (!this.authService.isAuthenticated() || !this.storesReady()) {
      return;
    }

    this.currentPractice = this.localStorage.getItem('selected_practice');
    if (!this.currentPractice) {
      // PracticeStore selected but localStorage cache absent — fall back to it.
      this.currentPractice = this.practiceStore.selectedPractice() ?? null;
    }
    if (!this.currentPractice) return;

    const cached = this.sessionStorage.getItem(this.getUserTabsKey(), []);
    this.tabs.set(this.normalizeTabs(cached));

    // Cold-init: derive active from URL so refresh keeps you on the right tab.
    this.activeTabIndex.set(this.deriveActiveFromUrl());

    this.cleanupTabs();
    this.loadTabs();

    // Unsubscribe any previous subscription (e.g. a prior login event
    // re-entered initializeTabs) before registering a new callback —
    // WebSocketService stores callbacks in a per-event Set, so a fresh
    // subscribe without an unsubscribe accumulates handlers and runs
    // loadTabs() N times per push.
    if (this.channelName) {
      this.pushService.unsubscribe(this.channelName, Events.UserTabsUpdated);
    }
    this.channelName = `xmedical_practice_${this.coreService.selectedPracticeId()}_user_${this.coreService.currentUser.userId}_tabs_events`;
    this.pushService.subscribe(this.channelName, Events.UserTabsUpdated, () => {
      this.loadTabs();
    });
  }

  /**
   * Practice switch: clear, navigate to dashboard, and re-init for new practice.
   * The previous URL referenced a patient from the old practice; not safe to
   * derive active tab from it in the new context.
   */
  private handlePracticeChange(newPractice: Practice): void {
    // Unsubscribe old channel
    if (this.channelName) {
      this.pushService.unsubscribe(this.channelName, Events.UserTabsUpdated);
      this.channelName = '';
    }

    // Clear in-memory state (session-storage for OLD practice is preserved)
    this.tabs.set([]);
    this.activeTabIndex.set(-1);

    // Bind new practice
    this.currentPractice = newPractice;

    // Old URL is invalid for the new practice — go home.
    this.router.navigateByUrl(this.defaultRoute);

    // Hydrate from new practice's session-storage cache (fast restore).
    const cached = this.sessionStorage.getItem(this.getUserTabsKey(), []);
    this.tabs.set(this.normalizeTabs(cached));

    // No URL-derivation here — we just navigated to /dashboard.
    this.activeTabIndex.set(-1);

    this.cleanupTabs();
    this.loadTabs();

    // Subscribe new Pusher channel
    this.channelName = `xmedical_practice_${newPractice.id}_user_${this.coreService.currentUser.userId}_tabs_events`;
    this.pushService.subscribe(this.channelName, Events.UserTabsUpdated, () => {
      this.loadTabs();
    });

    this.tabBehaviourSubject.next(new TabEvent('tabs_loaded'));
  }

  private loadTabs(): void {
    const token = ++this.practiceLoadToken;
    this.userTabsRepository.getTabs().subscribe({
      next: (incoming) => {
        // Race guard: practice was switched while this request was in flight.
        if (token !== this.practiceLoadToken) return;

        const incomingTabs = this.normalizeTabs(incoming);

        // Echo guard: identical to current state — skip side effects entirely.
        if (TabsStore.tabListsEqual(this.tabs(), incomingTabs)) return;

        // Preserve active-tab identity across pusher-driven replacements.
        const previousActive = this.activeTab();
        this.tabs.set(incomingTabs);
        this.sessionStorage.setItem(this.getUserTabsKey(), incomingTabs);

        // Reconcile active index without yanking the user away.
        if (previousActive) {
          const newIdx = incomingTabs.findIndex(t => TabsStore.areTabsEqual(t, previousActive));
          this.activeTabIndex.set(newIdx);
        }

        this.cleanupTabs();
      },
      error: (err) => console.error(err),
    });
  }

  /**
   * Eviction enforcing maxTabs. Skips the active tab — if the oldest tab IS
   * the active one, evict the next-oldest instead.
   */
  private enforceMaxTabs(): void {
    if (this.maxTabs === -1) return;

    const tabs = [...this.tabs()];
    let activeIndex = this.activeTabIndex();
    let changed = false;

    while (tabs.length > this.maxTabs) {
      const evictIdx = tabs.findIndex((_, i) => i !== activeIndex);
      if (evictIdx === -1) break; // only the active tab remains

      const removed = tabs[evictIdx];
      tabs.splice(evictIdx, 1);
      changed = true;

      if (evictIdx < activeIndex) activeIndex -= 1;

      // Clean up the evicted tab's per-patient session-storage entry.
      this.removePatientFromStorage(removed.profileId);
    }

    if (!changed) return;

    this.tabs.set(tabs);
    this.activeTabIndex.set(activeIndex);
    this.saveTabs();
  }

  private cleanupTabs(): void {
    const tabs = this.tabs().filter(t => t != null);

    // Apply maxTabs while protecting the active tab.
    let activeIndex = this.activeTabIndex();
    if (this.maxTabs !== -1 && tabs.length > this.maxTabs) {
      while (tabs.length > this.maxTabs) {
        const evictIdx = tabs.findIndex((_, i) => i !== activeIndex);
        if (evictIdx === -1) break;
        tabs.splice(evictIdx, 1);
        if (evictIdx < activeIndex) activeIndex -= 1;
      }
    }

    this.tabs.set(tabs);

    // Don't snap the active index back to 0 unless it's now invalid.
    if (activeIndex >= tabs.length || activeIndex < 0) {
      activeIndex = -1;
    }
    this.activeTabIndex.set(activeIndex);

    this.tabBehaviourSubject.next(new TabEvent('tabs_loaded'));
  }

  private deriveActiveFromUrl(): number {
    const url = this.router.url || '';
    const match = url.match(/\/patients\/switch\/(\d+)/);
    if (!match) return -1;
    const profileId = Number(match[1]);
    return this.tabs().findIndex(t => t.profileId === profileId);
  }

  private removePatientFromStorage(profileId: number): void {
    if (!this.currentPractice) return;
    const key = `patient-key-${profileId}-${this.currentPractice.id}`;
    this.sessionStorage.deleteItem(key);
  }

  private getUserTabsKey(): string {
    return `user-tabs-${this.currentPractice.id}`;
  }

  private ensureInitialized(): void {
    if (!this.currentPractice) {
      this.initializeTabs();
    }
  }

  private saveTabs(): void {
    if (!this.currentPractice) return;
    const tabs = this.tabs();
    this.sessionStorage.setItem(this.getUserTabsKey(), tabs);
    this.userTabsRepository.updateTabs(tabs).subscribe({
      next: () => {},
      error: (err) => {
        this.coreService.showErrorToast(err);
        console.error(err);
      },
    });
  }

  private setupMaxTabs(): void {
    this.maxTabs = this.coreService.settings()?.maxTabs ?? -1;
  }

  private normalizeTabs(input: TabInterface[] | null | undefined): Tab[] {
    if (!input) return [];
    return input
      .filter(t => t != null)
      .map(t => new Tab(t.title, t.patientId, t.profileId, t.route));
  }

  private static areTabsEqual(a: Tab | null, b: Tab | null): boolean {
    return a !== null && b !== null && a.patientId === b.patientId && a.profileId === b.profileId;
  }

  private static tabListsEqual(a: Tab[], b: Tab[]): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (
        a[i]?.patientId !== b[i]?.patientId ||
        a[i]?.profileId !== b[i]?.profileId ||
        a[i]?.route !== b[i]?.route ||
        a[i]?.title !== b[i]?.title
      ) return false;
    }
    return true;
  }
}

export class Tab {
  patientId: number;
  profileId: number;
  title: string;
  route: string;

  constructor(title: string, patientId: number, profileId: number, route: string) {
    this.title = title;
    this.route = (route || '').split(' ').join('-');
    this.patientId = patientId;
    this.profileId = profileId;
  }
}

export type TabEventType =
  | 'tab_service_initialized'
  | 'tabs_loaded'
  | 'tab_updated'
  | 'tab_created'
  | 'tab_closed'
  | 'tab_activated';

export class TabEvent {
  constructor(readonly type: TabEventType) {}
}
